import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store/types/sharedTypes';
import {
  Accordion,
  Banner,
  Body,
  Button,
  Group,
  H7,
  Icon,
  Stack,
} from '@qred/components-library';
import { onChangeInputEvent, onFocusSelectEvent } from '~/types/types';
import { ValidationContext } from '~/components/hoc/withValidation';
import useModals from '~/hooks/useModals';
import useOnboardingMarketConfig from '~/hooks/useOnboardingMarketConfig';
import { useCardOnboardingOfferSelectors } from '~/store/selectors/cardOnboardingOffer.selector';
import { validateFetchedBeneficialOwner } from '~/helpers/onboarding.helper';
import BeneficialOwner from './BeneficialOwner';
import OwnerManagementModal from '../OwnershipAdditionalOwnersPanel/OwnerManagementModal';
import OwnershipUploadDocumentationPanel from '../OwnershipUploadDocumentationPanel/OwnershipUploadDocumentationPanel';
import { pushToGtmCardOnboardingAction } from '~/store/actions/gtmActions';

const FetchedBeneficialOwners = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();

  const validationContext = useContext(ValidationContext);
  const {
    cardOnboardingOffer: { form },
    matchedMedia: { mobile },
  } = useSelector((state: RootState) => state);

  const {
    marketHasFetchedOwnershipSharesAsRanges,
    marketRequiresPersonalNumberForFetchedOwners,
    marketRequiresDateOfBirthForFetchedOwners,
    marketRequiresYearOfBirthForFetchedOwners,
  } = useOnboardingMarketConfig();

  const {
    fetchedOwnersHaveBeenChanged,
    totalAmountOfShares,
  } = useCardOnboardingOfferSelectors();

  const findFirstInvalidAccordionItem = () => {
    const invalidBeneficialOwner = form.owners.find(
      (beneficialOwner) => !validateFetchedBeneficialOwner(beneficialOwner)
    );
    if (invalidBeneficialOwner) {
      return invalidBeneficialOwner.id.toString();
    }

    return null;
  };
  const [accordionValue, setAccordionValue] = useState<string | null>(
    findFirstInvalidAccordionItem()
  );

  const onBlur = (event: onChangeInputEvent | onFocusSelectEvent | string) => {
    let name;
    if (typeof event === 'string') {
      name = event;
    } else {
      name = event.target.name;
    }
    dispatch(
      pushToGtmCardOnboardingAction({
        actionName: `${name}_change`,
      })
    );
  };

  useEffect(() => {
    const fetchedBeneficialOwnersHasErrors = form.owners.some(
      (owner) => !validateFetchedBeneficialOwner(owner)
    );

    if (fetchedBeneficialOwnersHasErrors) {
      validationContext.addPropertyToValidationErrors(
        'fetchedBeneficialOwners'
      );
    }

    const firstInvalidAccordionItem = findFirstInvalidAccordionItem();
    setAccordionValue(firstInvalidAccordionItem);

    return () => {
      validationContext.removePropertyFromValidationErrors(
        'fetchedBeneficialOwners'
      );
    };
  }, [form.owners, totalAmountOfShares]);

  const modals = useModals();

  const openOwnerManagementModal = () => {
    dispatch(
      pushToGtmCardOnboardingAction({
        actionName: 'card_offer_add_owner',
      })
    );

    modals.openModal({
      header: <H7>{translate('OnboardingOffer.AddNewOwner')}</H7>,
      children: (
        <OwnerManagementModal
          ownershipPercentageAsRanges={marketHasFetchedOwnershipSharesAsRanges}
          askForPersonalNumber={marketRequiresPersonalNumberForFetchedOwners}
          askForDateOfBirth={marketRequiresDateOfBirthForFetchedOwners}
          askForYearOfBirth={marketRequiresYearOfBirthForFetchedOwners}
        />
      ),
      size: 'md',
      fullScreen: mobile,
      filledBackground: true,
      hasOwnFooter: true,
    });
  };

  const applicantCanAddAdditionalBeneficialOwners = totalAmountOfShares < 75;

  return (
    <>
      {!!form.owners?.length && (
        <Accordion value={accordionValue} onChange={setAccordionValue}>
          {form.owners.map((beneficialOwner) => (
            <Accordion.Item
              value={beneficialOwner.id.toString()}
              key={beneficialOwner.id}
            >
              <Accordion.Control>
                <Group align="center">
                  <Icon
                    src={
                      validateFetchedBeneficialOwner(beneficialOwner)
                        ? 'CheckCircle'
                        : 'CheckCircleUnfilled'
                    }
                    size="lg"
                    iconSize={16}
                  />
                  {beneficialOwner.fullName}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <BeneficialOwner
                  key={beneficialOwner.id}
                  beneficialOwner={beneficialOwner}
                  onBlur={onBlur}
                />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {applicantCanAddAdditionalBeneficialOwners && (
        <Stack align="center">
          <Body size="lg">
            {translate('OnboardingOffer.FetchedBeneficialOwnersAddOwnerLabel')}
          </Body>
          <Button
            onClick={openOwnerManagementModal}
            variant="secondary"
            fullWidth={mobile}
          >
            <Icon src="Plus" mr="0.5em" />
            {translate(
              'OnboardingOffer.AdditionalOwnersPanelAddOwnerButtonLabel'
            )}
          </Button>
        </Stack>
      )}

      <Banner
        state="critical"
        withCloseButton={false}
        message={translate('OnboardingOffer.TotalOwnershipSharesMoreThan100')}
        opened={
          validationContext.validationErrors.totalOwnershipSharesMoreThan100
        }
      />

      {fetchedOwnersHaveBeenChanged && <OwnershipUploadDocumentationPanel />}
    </>
  );
};

export default FetchedBeneficialOwners;
